import React, { useMemo } from 'react';
import { Row, Col } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import getChartColorsArray from "../../../Components/Common/ChartsDynamicColor";


function Charts({ reportData, loading }) {
  const { severityData, lineSeries } = useMemo(() => {
    if (!reportData) return { severityData: [0, 0, 0, 0], lineSeries: [] };

    const summary = reportData.summary || {};
    const severityData = [
      Number(summary.critical_count) || 0,
      Number(summary.high_count)     || 0,
      Number(summary.medium_count)   || 0,
      Number(summary.low_count)      || 0,
    ];

    const monthly  = reportData.monthly || [];
    const monthMap = {};
    monthly.forEach(m => {
      const idx = new Date(m.month).getMonth();
      monthMap[idx] = {
        Critical: m.critical_count || 0,
        High:     m.high_count     || 0,
        Medium:   m.medium_count   || 0,
        Low:      m.low_count      || 0,
      };
    });

    const criticals = [], highs = [], mediums = [], lows = [];
    for (let i = 0; i < 12; i++) {
      criticals.push(monthMap[i]?.Critical || 0);
      highs.push(monthMap[i]?.High         || 0);
      mediums.push(monthMap[i]?.Medium      || 0);
      lows.push(monthMap[i]?.Low            || 0);
    }

    return {
      severityData,
      lineSeries: [
        { name: "Critical Threats", data: criticals },
        { name: "High Threats",     data: highs     },
        { name: "Medium Threats",   data: mediums   },
        { name: "Low Threats",      data: lows      },
      ],
    };
  }, [reportData]);

  const hasData = severityData.some(v => v > 0);

  const SimplePie = ({ dataColors, series }) => {
    const chartPieBasicColors = getChartColorsArray(dataColors);
    const options = {
      chart: { type: 'pie', height: 260, background: "#ffffff", toolbar: { show: false } },
      labels: ['Critical', 'High', 'Medium', 'Low'],
      legend: {
        position: 'bottom',
        labels: { colors: "#374151" },
        markers: { fillColors: chartPieBasicColors },
        fontSize: '12px',
      },
      theme: { mode: 'light' },
      dataLabels: {
        enabled: true,
        style: { fontWeight: 600, fontSize: "12px", colors: ["#fff"] },
        dropShadow: { enabled: false },
        formatter: (val) => `${Math.round(val)}%`,
      },
      colors: chartPieBasicColors,
      tooltip: { theme: "light" },
      stroke: { show: false },
      plotOptions: { pie: { donut: { size: '0%' } } },
    };
    return (
      <ReactApexChart dir="ltr" className="apex-charts" series={series} options={options} type="pie" height={260} />
    );
  };

  const BasicLineCharts = ({ dataColors, series }) => {
    const linechartBasicColors = getChartColorsArray(dataColors);
    const allValues = series.flatMap(s => s.data);
    const safeMaxY = Math.max(...allValues, 10);

    const options = {
      chart: {
        height: 260, type: 'line', background: "#ffffff",
        zoom: { enabled: false }, toolbar: { show: false },
      },
      grid: { borderColor: '#f1f1f1', strokeDashArray: 4 },
      markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2.5 },
      colors: linechartBasicColors,
      xaxis: {
        categories: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        labels: { style: { colors: "#6b7280", fontSize: '11px' } },
        axisBorder: { show: false }, axisTicks: { show: false },
      },
      yaxis: {
        min: 0, max: safeMaxY,
        labels: { style: { colors: "#6b7280", fontSize: '11px' } },
      },
      legend: {
        position: "top", fontSize: '12px',
        labels: { colors: "#374151" },
        markers: { fillColors: linechartBasicColors },
      },
      theme: { mode: 'light' },
      tooltip: { theme: "light" },
    };
    return (
      <ReactApexChart dir="ltr" options={options} series={series} type="line" height={260} className="apex-charts" />
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem 0", color: "#9ca3af", fontSize: 13 }}>
        Loading charts...
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 32 }}>
      <Row className="g-4">
        <Col md={5}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "20px 16px" }}>
            <h6 style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Severity Distribution
            </h6>
            {hasData
              ? <SimplePie dataColors='["#dc2626","#ea580c","#eab308","#16a34a"]' series={severityData} />
              : <div style={{ textAlign: "center", padding: "4rem 0", color: "#9ca3af", fontSize: 13 }}>No threat data available</div>
            }
          </div>
        </Col>
        <Col md={7}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "20px 16px" }}>
            <h6 style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Monthly Threat Trends
            </h6>
            <BasicLineCharts dataColors='["#dc2626","#ea580c","#eab308","#16a34a"]' series={lineSeries} />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default Charts;
